import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { OrderState, CartItem, MenuItem } from '../types';
import menuService from '../data/menuService';

// Async thunk for loading menu data
export const loadMenuData = createAsyncThunk(
  'order/loadMenuData',
  async (_, { rejectWithValue }) => {
    try {
      await menuService.initializeMenuData();
      
      const menuCategories = menuService.getMenuCategories();
      const menuItems = menuService.getAllMenuItems();
      const firstCategoryId = menuService.getFirstCategoryId();

      return {
        menuCategories,
        menuItems,
        firstCategoryId
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load menu data');
    }
  }
);

// Async thunk for refreshing menu data
export const refreshMenuData = createAsyncThunk(
  'order/refreshMenuData',
  async (_, { rejectWithValue }) => {
    try {
      await menuService.refreshMenuData();
      
      const menuCategories = menuService.getMenuCategories();
      const menuItems = menuService.getAllMenuItems();
      const firstCategoryId = menuService.getFirstCategoryId();

      return {
        menuCategories,
        menuItems,
        firstCategoryId
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to refresh menu data');
    }
  }
);

const initialState: OrderState = {
  diningOption: null,
  cart: [],
  currentScreen: 'welcome',
  selectedCategory: '',
  selectedMenuItem: null,
  menuCategories: [],
  menuItems: [],
  isLoading: false,
  error: null,
  loyaltyCode: null,
  currentOrderNumber: null,
  currentOrderTimestamp: null,
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    setDiningOption: (state, action: PayloadAction<'eat-in' | 'takeaway'>) => {
      state.diningOption = action.payload;
      state.currentScreen = 'menu';
    },
    setCurrentScreen: (state, action: PayloadAction<OrderState['currentScreen']>) => {
      state.currentScreen = action.payload;
    },
    setSelectedCategory: (state, action: PayloadAction<string>) => {
      state.selectedCategory = action.payload;
    },
    setSelectedMenuItem: (state, action: PayloadAction<MenuItem | null>) => {
      state.selectedMenuItem = action.payload;
    },
    addToCart: (state, action: PayloadAction<CartItem>) => {
      const existingItem = state.cart.find(
        item => item.menuItem.id === action.payload.menuItem.id &&
        JSON.stringify(item.selectedOptions) === JSON.stringify(action.payload.selectedOptions)
      );
      
      if (existingItem) {
        existingItem.quantity += action.payload.quantity;
        existingItem.totalPrice += action.payload.totalPrice;
      } else {
        state.cart.push(action.payload);
      }
    },
    removeFromCart: (state, action: PayloadAction<string>) => {
      state.cart = state.cart.filter(item => item.id !== action.payload);
    },
    updateCartItemQuantity: (state, action: PayloadAction<{ id: string; quantity: number }>) => {
      const item = state.cart.find(item => item.id === action.payload.id);
      if (item) {
        item.quantity = action.payload.quantity;
        item.totalPrice = (item.menuItem.price +
          item.selectedOptions.reduce((sum, option) => sum + option.price, 0)) * action.payload.quantity;
      }
    },
    clearCart: (state) => {
      state.cart = [];
      state.currentOrderNumber = null;
      state.currentOrderTimestamp = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setLoyaltyCode: (state, action: PayloadAction<string | null>) => {
      state.loyaltyCode = action.payload;
    },
    setOrderMetadata: (state, action: PayloadAction<{ orderNumber: string; timestamp: string }>) => {
      state.currentOrderNumber = action.payload.orderNumber;
      state.currentOrderTimestamp = action.payload.timestamp;
    },
    clearOrderMetadata: (state) => {
      state.currentOrderNumber = null;
      state.currentOrderTimestamp = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Load menu data
      .addCase(loadMenuData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadMenuData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.menuCategories = action.payload.menuCategories;
        state.menuItems = action.payload.menuItems;
        if (!state.selectedCategory && action.payload.firstCategoryId) {
          state.selectedCategory = action.payload.firstCategoryId;
        }
        state.error = null;
      })
      .addCase(loadMenuData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Refresh menu data
      .addCase(refreshMenuData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshMenuData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.menuCategories = action.payload.menuCategories;
        state.menuItems = action.payload.menuItems;
        state.error = null;
      })
      .addCase(refreshMenuData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setDiningOption,
  setCurrentScreen,
  setSelectedCategory,
  setSelectedMenuItem,
  addToCart,
  removeFromCart,
  updateCartItemQuantity,
  clearCart,
  clearError,
  setLoyaltyCode,
  setOrderMetadata,
  clearOrderMetadata,
} = orderSlice.actions;

export default orderSlice.reducer; 